<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import long2ip from 'locutus/php/network/long2ip'
import PageNavigation from '@components/admin/page-navigation'
import { fetchMerchantList } from '@apis/admin/pg'
import fromPairs from 'lodash/fromPairs'
import map from 'lodash/map'
import mapKeys from 'lodash/mapKeys'
import pick from 'lodash/pick'

export default {
  components: { PageNavigation },
  data() {
    return {
      loading: false,
      list: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      show_merchant_info: false,
      target_merchant_seq: 0,
      excel_headers: [
        ['seq', this.$t('pg_merchant_list.seq')],
        ['member_seq', this.$t('pg_merchant_list.member_seq')],
        ['user_id', this.$t('pg_merchant_list.user_id')],
        ['user_name', this.$t('pg_merchant_list.user_name')],
        ['email_address', this.$t('pg_merchant_list.email_address')],
        ['merchant_id', this.$t('pg_merchant_list.merchant_id')],
        ['merchant_name', this.$t('pg_merchant_list.merchant_name')],
        ['api_signature', this.$t('pg_merchant_list.api_signature')],
        ['status', this.$t('pg_merchant_list.status')],
      ],
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchMerchantList({
        ...this.$route.params,
        ...this.$route.query,
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = pick(this.search_params, 'market_seq')
      this.search()
    },
    date,
    fromPairs,
    long2ip,
    handleCreateMerchant() {
      this.show_merchant_info = true
      this.target_merchant_seq = null
    },
    handleUpdateMerchant({ seq }) {
      this.show_merchant_info = true
      this.target_merchant_seq = seq
    },
    handleMerchantSubmit(seq) {
      this.init()
      this.target_merchant_seq = seq
    },
    async handleExportExcel() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t('pg_merchant_list.msg_server_overload'),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        const { data } = await fetchMerchantList({
          ...this.$route.params,
          ...this.$route.query,
          no_paging: 1,
        })

        const { default: exportJsonToExcel } = await import(
          '@utils/export-json-to-excel'
        )
        exportJsonToExcel({
          header: map(this.excel_headers, ([, title]) => title),
          data: this.format2excel(data),
          filename: this.$t('pg_merchant_list.list_of_stores', [
            date('YmdHis'),
          ]),
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    format2excel(list) {
      const excel_fields = map(this.excel_headers, ([name]) => name)
      return map(list, (data) => {
        let result = pick(data, excel_fields)
        const keys = Object.keys(result)
        result = mapKeys(
          map(result, (val, key) => {
            switch (key) {
              case 'regdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              default:
                return val
            }
          }),
          (val, key) => {
            return keys[key]
          }
        )

        return map(excel_fields, (key) => result[key])
      })
    },
  },
}
</script>

<template>
  <div v-loading="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['user_id', $t('admin.user_id')],
                      ['email_address', $t('admin.email_address')],
                      ['user_name', $t('admin.user_name')],
                      ['merchant_id', $t('wallet.merchant_id')],
                      ['merchant_name', $t('wallet.merchant_name')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{
                  $t('pg_merchant_list.statistics_exclude_members')
                }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.member_is_alive"
                :false-label="undefined"
                true-label="Y"
                >{{
                  $t('pg_merchant_list.withdrawal_members_excluded')
                }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td colspan="3">
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [[undefined, $t('admin.regdate')]]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="clearAndSearch"
                ><span>{{ $t('admin.reset') }}</span></a
              >
              {{ ' ' }}
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('pg_merchant_list.list_of_stores') }}</h3>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent="handleExportExcel"
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
      <div :class="$style.fr">
        <a
          v-if="$route.params.member_seq"
          href="#"
          :class="[$style.button, $style.green]"
          @click.prevent="handleCreateMerchant"
          ><span>{{ $t('admin.new_merchant') }}</span></a
        >
      </div>
    </fieldset>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <template v-if="!$route.params.member_seq">
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_id') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_name') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.email_address') }}</div></th
            >
          </template>
          <th scope="col"
            ><div>{{ $t('pg_merchant_list.store_name') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('pg_merchant_list.merchant_id') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('pg_merchant_list.status') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('pg_merchant_list.registration_date') }}</div></th
          >
          <th scope="col"><div>&nbsp;</div></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(data, idx) in list">
          <tr :key="data.seq" :class="$style[`bg${idx % 2}`]">
            <td :class="[$style.tCenter, $style.nowrap]">{{ data._no }}</td>
            <template v-if="!$route.params.member_seq">
              <td :class="[$style.tCenter, $style.nowrap]">{{
                data.user_id
              }}</td>
              <td :class="[$style.tCenter, $style.nowrap]">{{
                data.user_name
              }}</td>
              <td :class="[$style.tCenter, $style.nowrap]">{{
                data.email_address
              }}</td>
            </template>
            <td :class="[$style.wide]">{{ data.merchant_name }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.merchant_id
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              fromPairs(constants.pg.merchant.status)[data.status]
            }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              date('Y-m-d H:i:s', new Date(data.regdate))
            }}</td>
            <td :class="[$style.tRight, $style.nowrap]">
              <router-link
                :to="{
                  name: 'admin-pg-merchant-catalog',
                  params: { merchant_seq: data.seq },
                }"
                :class="[$style.button, $style.gray]"
                @click.prevent
                ><span>{{
                  $t('pg_merchant_list.store_details')
                }}</span></router-link
              >
            </td>
          </tr>
        </template>
        <tr v-if="loading">
          <td
            :colspan="$route.params.member_seq ? 8 : 10"
            :class="$style.tCenter"
            >{{ $t('admin.loading') }}</td
          >
        </tr>
        <tr v-else-if="!list.length">
          <td
            :colspan="$route.params.member_seq ? 8 : 10"
            :class="$style.tCenter"
            >{{ $t('admin.no_search_merchant') }}</td
          >
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}
</style>
